
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AnimatedPage from "@/components/AnimatedPage";
import LoanForm from "@/components/LoanForm";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loan } from "@/lib/types";
import { getLoanById } from "@/lib/api";
import NavHeader from "@/components/NavHeader";

interface LoanDetailsProps {}

const LoanDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loan, setLoan] = useState<Loan | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLoan = async () => {
      if (!id) return;
      
      try {
        const loanData = await getLoanById(id);
        setLoan(loanData);
      } catch (error) {
        console.error("Error fetching loan:", error);
        toast.error("Failed to load loan details");
        navigate("/loans");
      } finally {
        setLoading(false);
      }
    };

    fetchLoan();
  }, [id, navigate]);

  if (loading) {
    return (
      <AnimatedPage>
        <div className="container max-w-3xl py-8 flex justify-center items-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading loan details...</p>
          </div>
        </div>
      </AnimatedPage>
    );
  }

  if (!loan) {
    return (
      <AnimatedPage>
        <div className="container max-w-3xl py-8 space-y-8">
          <NavHeader
            title="Loan Not Found"
            backTo="/loans"
          />
          
          <div className="text-center py-16">
            <h3 className="text-lg font-medium mb-2">The loan could not be found</h3>
            <p className="text-muted-foreground mb-6">
              The loan may have been deleted or the ID is invalid.
            </p>
            <Button onClick={() => navigate("/loans")}>
              Return to Loans
            </Button>
          </div>
        </div>
      </AnimatedPage>
    );
  }

  // Ensure we have the description field
  const loanWithDescription = {
    ...loan,
    description: loan.description || ""
  };

  return (
    <AnimatedPage>
      <div className="container max-w-3xl py-8 space-y-8">
        <NavHeader
          title={`Edit: ${loan.customer_name}`}
          subtitle="Update loan details and payment information"
          backTo="/loans"
        />
        
        <div className="rounded-lg border bg-card p-6 shadow-sm">
          {/* Using the spread operator instead of named prop to pass all loan properties directly */}
          <LoanForm {...loanWithDescription} isEditing={true} />
        </div>
      </div>
    </AnimatedPage>
  );
};

export default LoanDetails;
